/**
 * LEER UN DOCUMENTO QUE LA PERSONA SUBE AL ASISTENTE (28-09-2026).
 *
 * Pablo: «si le exporto algún documento, que te lo rellene, que te lo entregue
 * limpio, que lo ordene por día», «estar preparado para recibir CSV, Excel y todo
 * eso, y que lo pueda leer bien» (programadores de riego, fechas de cosecha,
 * plantillas de la cooperativa o de la certificadora).
 *
 * Aquí se LEE en el navegador y se deja en una forma común para las tres clases de
 * fichero: hojas (Excel, CSV) y tablas (Word) como matrices de celdas ya
 * NORMALIZADAS, que es lo que ve el asistente:
 *   · números como número (un CSV español «1.234,5» es 1234.5; «0012» se queda texto:
 *     un código o un NIF no es una cantidad);
 *   · fechas como «AAAA-MM-DD» (o «AAAA-MM-DD HH:MM»), vengan como fecha de Excel,
 *     «28/09/2026» o «2026-09-28»;
 *   · texto recortado; vacío = null.
 * Y se guarda DÓNDE empieza cada hoja (`origen`) y cada tabla (`indice`): el relleno
 * escribe en el ORIGINAL, en su sitio (rellenar.ts), para devolver la MISMA plantilla.
 *
 * Los CSV de los programadores de riego (Agrónic, Priva…) salen con punto y coma,
 * coma decimal y a veces en Windows-1252: se detecta el separador por coherencia de
 * columnas y la codificación probando UTF-8 estricto primero.
 *
 * Lo pesado (SheetJS, fflate) se carga al subir, no antes.
 */

export type TipoDocumento = "xlsx" | "xls" | "csv" | "docx";
export type Celda = string | number | null;

export interface HojaLeida {
  /** Nombre de la hoja; en Word, «Tabla 1», «Tabla 2»…; en un CSV, el del fichero. */
  nombre: string;
  tipo: "hoja" | "tabla";
  /** Filas × columnas, normalizadas (ver cabecera). Recortadas a los topes. */
  celdas: Celda[][];
  filasTotales: number;
  columnasTotales: number;
  /** true = había más filas o columnas de las que se guardan. */
  recortada: boolean;
  /** Excel: fila y columna (base 0) de la primera celda de `celdas` en la hoja. */
  origen?: { fila: number; columna: number };
  /** Word: posición de la tabla entre las tablas del cuerpo (base 0). */
  indice?: number;
}

export interface CampoLeido {
  /** «Titular», «NIF», «Campaña»… (lo que va antes de los dos puntos). */
  etiqueta: string;
  /** Word: índice del párrafo en el cuerpo (base 0). */
  parrafo: number;
  /** Lo que ya pone (vacío si es una línea para rellenar). */
  valor: string;
}

export interface DocumentoLeido {
  v: 1;
  nombre: string;
  tipo: TipoDocumento;
  hojas: HojaLeida[];
  /** Word: los párrafos de fuera de las tablas (recortados). */
  texto?: string[];
  /** Word: las líneas «Etiqueta: ____» (para rellenar los datos de cabecera). */
  campos?: CampoLeido[];
}

/** Topes: lo que se guarda y lo que ve el asistente. Un mes de riego de 40 sectores son ~1.200 filas. */
export const MAX_FILAS = 5_000;
export const MAX_COLUMNAS = 60;
export const MAX_HOJAS = 12;
export const MAX_TEXTO_CELDA = 500;
export const MAX_PARRAFOS = 400;
/** El contenido leído entero, en JSON (la tabla admite 4 MB). */
export const MAX_JSON = 3_500_000;
/** El fichero original (el bucket admite 15 MB). */
export const MAX_BYTES = 15 * 1024 * 1024;

export const ACEPTA = ".xlsx,.xlsm,.xls,.csv,.txt,.docx";

/** «plantilla.xlsx» → xlsx; null = no se sabe leer (todavía: PDF). */
export function tipoDeFichero(nombre: string): TipoDocumento | null {
  const ext = nombre.toLowerCase().split(".").pop() ?? "";
  if (ext === "xlsx" || ext === "xlsm") return "xlsx";
  if (ext === "xls") return "xls";
  if (ext === "csv" || ext === "txt") return "csv";
  if (ext === "docx") return "docx";
  return null;
}

/* ── Normalizar una celda de texto ───────────────────────────────────────── */

const DIA_MES_ANIO = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})(?:[ T](\d{1,2}):(\d{2})(?::\d{2})?)?$/;
const ANIO_MES_DIA = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

const dos = (n: number) => String(n).padStart(2, "0");

function fechaValida(a: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31 || a < 1900 || a > 2200) return false;
  const f = new Date(Date.UTC(a, m - 1, d));
  return f.getUTCMonth() === m - 1 && f.getUTCDate() === d;
}

/** «28/09/2026», «28-9-26 14:30», «2026-09-28» → ISO; null si no es una fecha. Día/mes a la española. */
export function fechaDeTexto(s: string): string | null {
  const t = s.trim();
  let m = ANIO_MES_DIA.exec(t);
  if (m) {
    const [a, mes, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (!fechaValida(a, mes, d)) return null;
    return m[4] != null ? `${a}-${dos(mes)}-${dos(d)} ${dos(Number(m[4]))}:${m[5]}` : `${a}-${dos(mes)}-${dos(d)}`;
  }
  m = DIA_MES_ANIO.exec(t);
  if (m) {
    const d = Number(m[1]);
    const mes = Number(m[2]);
    let a = Number(m[3]);
    if (m[3].length === 2) a += a < 70 ? 2000 : 1900;
    if (!fechaValida(a, mes, d)) return null;
    return m[4] != null ? `${a}-${dos(mes)}-${dos(d)} ${dos(Number(m[4]))}:${m[5]}` : `${a}-${dos(mes)}-${dos(d)}`;
  }
  return null;
}

/**
 * Un número escrito a la española o a la inglesa → número; null si no lo es con
 * seguridad. `decimalComa` = el fichero usa coma decimal (CSV con «;»).
 * «0012» no es número (código); «1.234» con coma decimal es 1234; sin ella, 1.234.
 */
export function numeroDeTexto(s: string, decimalComa: boolean): number | null {
  let t = s.trim().replace(/\u00a0|\u202f/g, " ");
  if (!t || t.length > 20) return null;
  let signo = 1;
  if (/^[-−]/.test(t)) {
    signo = -1;
    t = t.slice(1).trim();
  }
  if (!/^[\d.,\s]+$/.test(t) || !/\d/.test(t)) return null;
  // Ceros a la izquierda = código (salvo «0», «0,5»…).
  if (/^0\d/.test(t.replace(/\s/g, ""))) return null;
  t = t.replace(/\s/g, "");
  let n: number;
  if (decimalComa) {
    // «1.234,56» o «12,5» o «1234»
    if (!/^\d{1,3}(\.\d{3})*(,\d+)?$/.test(t) && !/^\d+(,\d+)?$/.test(t)) return null;
    n = Number(t.replace(/\./g, "").replace(",", "."));
  } else {
    // «1,234.56» o «12.5» o «1234»; una coma sola con 1-2 decimales («12,5») también vale.
    if (/^\d+,\d{1,2}$/.test(t)) n = Number(t.replace(",", "."));
    else if (/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(t) || /^\d+(\.\d+)?$/.test(t)) n = Number(t.replace(/,/g, ""));
    else return null;
  }
  return Number.isFinite(n) ? signo * n : null;
}

/** Texto de una celda → celda normalizada. */
export function normalizarTexto(raw: string, decimalComa: boolean): Celda {
  const s = raw.replace(/\r/g, "").trim();
  if (!s) return null;
  const f = fechaDeTexto(s);
  if (f) return f;
  const n = numeroDeTexto(s, decimalComa);
  if (n != null) return n;
  return s.length > MAX_TEXTO_CELDA ? s.slice(0, MAX_TEXTO_CELDA) : s;
}

/* ── CSV ─────────────────────────────────────────────────────────────────── */

/** Bytes → texto: UTF-8 si lo es de verdad; si no, Windows-1252 (los exportes de Windows). */
export function decodificar(bytes: Uint8Array): string {
  let texto: string;
  try {
    texto = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    texto = new TextDecoder("windows-1252").decode(bytes);
  }
  return texto.replace(/^\uFEFF/, "");
}

/** Parte un CSV con comillas («"a;b";c»), sin perder saltos dentro de comillas. PURA. */
export function partirCsv(texto: string, sep: string): string[][] {
  const filas: string[][] = [];
  let fila: string[] = [];
  let celda = "";
  let comillas = false;
  for (let i = 0; i < texto.length; i++) {
    const ch = texto[i];
    if (comillas) {
      if (ch === '"') {
        if (texto[i + 1] === '"') {
          celda += '"';
          i++;
        } else comillas = false;
      } else celda += ch;
    } else if (ch === '"' && celda === "") comillas = true;
    else if (ch === sep) {
      fila.push(celda);
      celda = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && texto[i + 1] === "\n") i++;
      fila.push(celda);
      filas.push(fila);
      fila = [];
      celda = "";
    } else celda += ch;
  }
  if (celda !== "" || fila.length) {
    fila.push(celda);
    filas.push(fila);
  }
  return filas;
}

/** El separador que deja las mismas columnas en más líneas (las primeras 30). «sep=;» manda. */
export function detectarSeparador(texto: string): string {
  const primera = texto.split(/\r?\n/, 1)[0] ?? "";
  const pedido = /^sep=(.)$/i.exec(primera.trim());
  if (pedido) return pedido[1];
  const muestra = texto.split(/\r?\n/).filter((l) => l.trim()).slice(0, 30).join("\n");
  let mejor = ",";
  let puntos = -1;
  for (const sep of [";", ",", "\t", "|"]) {
    const filas = partirCsv(muestra, sep).filter((f) => f.length > 1);
    if (!filas.length) continue;
    const cuenta = new Map<number, number>();
    for (const f of filas) cuenta.set(f.length, (cuenta.get(f.length) ?? 0) + 1);
    const [cols, veces] = [...cuenta.entries()].sort((a, b) => b[1] - a[1])[0];
    const p = veces * 100 + cols;
    if (p > puntos) {
      puntos = p;
      mejor = sep;
    }
  }
  return mejor;
}

/** Recorta filas vacías al final y columnas vacías a la derecha, y aplica los topes. */
function recortar(celdas: Celda[][]): { celdas: Celda[][]; filasTotales: number; columnasTotales: number; recortada: boolean } {
  let ultimaFila = celdas.length - 1;
  while (ultimaFila >= 0 && celdas[ultimaFila].every((c) => c == null)) ultimaFila--;
  const utiles = celdas.slice(0, ultimaFila + 1);
  let columnasTotales = 0;
  for (const f of utiles) {
    for (let c = f.length - 1; c >= 0; c--) {
      if (f[c] != null) {
        columnasTotales = Math.max(columnasTotales, c + 1);
        break;
      }
    }
  }
  const filasTotales = utiles.length;
  const cols = Math.min(columnasTotales, MAX_COLUMNAS);
  const out = utiles.slice(0, MAX_FILAS).map((f) => Array.from({ length: cols }, (_, c) => f[c] ?? null));
  return { celdas: out, filasTotales, columnasTotales, recortada: filasTotales > MAX_FILAS || columnasTotales > MAX_COLUMNAS };
}

export function leerCsv(nombre: string, bytes: Uint8Array): DocumentoLeido {
  const texto = decodificar(bytes);
  const sep = detectarSeparador(texto);
  const cuerpo = /^sep=.\r?\n/i.test(texto) ? texto.replace(/^sep=.\r?\n/i, "") : texto;
  const decimalComa = sep !== ",";
  const filas = partirCsv(cuerpo, sep).map((f) => f.map((c) => normalizarTexto(c, decimalComa)));
  const r = recortar(filas);
  return {
    v: 1,
    nombre,
    tipo: "csv",
    hojas: [{ nombre: nombre.replace(/\.[^.]+$/, ""), tipo: "hoja", ...r, origen: { fila: 0, columna: 0 } }],
  };
}

/* ── Excel ───────────────────────────────────────────────────────────────── */

type XlsxModule = typeof import("xlsx");

/** Una celda de SheetJS → celda normalizada (fecha de Excel → ISO sin pasar por la zona horaria). */
export function celdaDeExcel(XLSX: XlsxModule, cell: { t?: string; v?: unknown; z?: unknown; w?: string } | undefined): Celda {
  if (!cell || cell.v == null) return null;
  // SSF va con nombre en el build del navegador; en el de Node, solo en `default`.
  const SSF = XLSX.SSF ?? (XLSX as unknown as { default?: XlsxModule }).default?.SSF;
  if (cell.t === "n") {
    const v = Number(cell.v);
    const fmt = typeof cell.z === "string" ? cell.z : "";
    if (fmt && SSF?.is_date(fmt)) {
      const p = SSF.parse_date_code(v);
      if (p && fechaValida(p.y, p.m, p.d)) {
        const hora = p.H || p.M ? ` ${dos(p.H)}:${dos(p.M)}` : "";
        return `${p.y}-${dos(p.m)}-${dos(p.d)}${hora}`;
      }
    }
    return Number.isFinite(v) ? v : null;
  }
  if (cell.t === "d" && cell.v instanceof Date) {
    const d = cell.v;
    return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
  }
  if (cell.t === "b") return cell.v ? "Sí" : "No";
  if (cell.t === "e") return null;
  // Texto: puede ser una fecha o un número escrito como texto («28/09/2026», «12,5»).
  return normalizarTexto(String(cell.v), true);
}

export async function leerExcel(nombre: string, bytes: Uint8Array, tipo: "xlsx" | "xls"): Promise<DocumentoLeido> {
  const XLSX = await import("xlsx");
  const wb = XLSX.read(bytes, { type: "array", cellDates: false, cellNF: true, cellText: false });
  const hojas: HojaLeida[] = [];
  for (const nombreHoja of wb.SheetNames.slice(0, MAX_HOJAS)) {
    const ws = wb.Sheets[nombreHoja];
    const ref = ws?.["!ref"];
    if (!ws || !ref) continue;
    const rango = XLSX.utils.decode_range(ref);
    const ultimaFila = Math.min(rango.e.r, rango.s.r + MAX_FILAS + 50);
    const ultimaCol = Math.min(rango.e.c, rango.s.c + MAX_COLUMNAS + 10);
    const celdas: Celda[][] = [];
    for (let r = rango.s.r; r <= ultimaFila; r++) {
      const fila: Celda[] = [];
      for (let c = rango.s.c; c <= ultimaCol; c++) {
        fila.push(celdaDeExcel(XLSX, ws[XLSX.utils.encode_cell({ r, c })] as never));
      }
      celdas.push(fila);
    }
    const rec = recortar(celdas);
    hojas.push({
      nombre: nombreHoja,
      tipo: "hoja",
      ...rec,
      filasTotales: Math.max(rec.filasTotales, rango.e.r - rango.s.r + 1 > MAX_FILAS ? rango.e.r - rango.s.r + 1 : rec.filasTotales),
      recortada: rec.recortada || rango.e.r - rango.s.r + 1 > MAX_FILAS + 50,
      origen: { fila: rango.s.r, columna: rango.s.c },
    });
  }
  return { v: 1, nombre, tipo, hojas };
}

/* ── Word ────────────────────────────────────────────────────────────────── */

const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

/** El texto de un nodo de Word (w:t, tabuladores y saltos). */
export function textoDeNodoWord(n: Element): string {
  let out = "";
  const recorrer = (x: Element) => {
    for (const hijo of Array.from(x.children)) {
      const local = hijo.localName;
      if (local === "t") out += hijo.textContent ?? "";
      else if (local === "tab") out += "\t";
      else if (local === "br" || local === "cr") out += "\n";
      else if (local === "p") {
        // Un párrafo nuevo dentro de una celda: salto ENTRE párrafos, no al final.
        if (out && !out.endsWith("\n")) out += "\n";
        recorrer(hijo);
      } else if (local !== "tbl") recorrer(hijo);
    }
  };
  recorrer(n);
  return out.replace(/\n+$/, "");
}

/** «Titular: ______», «NIF:», «Campaña: 2025/26» → campo; null si el párrafo no es una línea de campo. */
export function campoDeParrafo(texto: string): { etiqueta: string; valor: string } | null {
  const m = /^\s*([^:\n]{2,50}?)\s*:\s*(.*)$/.exec(texto);
  if (!m) return null;
  const etiqueta = m[1].trim();
  // Una frase larga con dos puntos no es un campo.
  if (etiqueta.split(/\s+/).length > 6) return null;
  const valor = m[2].replace(/[_.…\s]{2,}$/g, "").replace(/^[_.…\s]+$/, "").trim();
  return { etiqueta, valor };
}

export async function leerWord(nombre: string, bytes: Uint8Array): Promise<DocumentoLeido> {
  const { unzipSync, strFromU8 } = await import("fflate");
  const zip = unzipSync(bytes, { filter: (f) => f.name === "word/document.xml" });
  const xml = zip["word/document.xml"];
  if (!xml) throw new Error("docx_sin_documento");
  const doc = new DOMParser().parseFromString(strFromU8(xml), "application/xml");
  const body = doc.getElementsByTagNameNS(W_NS, "body")[0];
  if (!body) throw new Error("docx_sin_cuerpo");
  const hojas: HojaLeida[] = [];
  const texto: string[] = [];
  const campos: CampoLeido[] = [];
  let parrafo = 0;
  let tablas = 0;
  for (const hijo of Array.from(body.children)) {
    if (hijo.localName === "p") {
      const t = textoDeNodoWord(hijo).trim();
      if (t && texto.length < MAX_PARRAFOS) texto.push(t.slice(0, MAX_TEXTO_CELDA));
      const c = t ? campoDeParrafo(t) : null;
      if (c && campos.length < 80) campos.push({ ...c, parrafo });
      parrafo++;
    } else if (hijo.localName === "tbl") {
      const filas: Celda[][] = [];
      for (const tr of Array.from(hijo.children).filter((x) => x.localName === "tr")) {
        filas.push(Array.from(tr.children).filter((x) => x.localName === "tc").map((tc) => normalizarTexto(textoDeNodoWord(tc), true)));
      }
      const rec = recortar(filas);
      if (hojas.length < MAX_HOJAS) hojas.push({ nombre: `Tabla ${tablas + 1}`, tipo: "tabla", ...rec, indice: tablas });
      tablas++;
    }
  }
  return { v: 1, nombre, tipo: "docx", hojas, texto, campos };
}

/* ── Entrada única ───────────────────────────────────────────────────────── */

export class DocumentoNoLeible extends Error {
  constructor(public motivo: "tipo" | "tamano" | "vacio" | "roto") {
    super(motivo);
  }
}

/** Lee el fichero y recorta hasta que quepa. Lanza DocumentoNoLeible con el motivo. */
export async function leerDocumento(nombre: string, bytes: Uint8Array): Promise<DocumentoLeido> {
  const tipo = tipoDeFichero(nombre);
  if (!tipo) throw new DocumentoNoLeible("tipo");
  if (bytes.length === 0) throw new DocumentoNoLeible("vacio");
  if (bytes.length > MAX_BYTES) throw new DocumentoNoLeible("tamano");
  let leido: DocumentoLeido;
  try {
    leido = tipo === "csv" ? leerCsv(nombre, bytes) : tipo === "docx" ? await leerWord(nombre, bytes) : await leerExcel(nombre, bytes, tipo);
  } catch (e) {
    if (e instanceof DocumentoNoLeible) throw e;
    throw new DocumentoNoLeible("roto");
  }
  if (!leido.hojas.some((h) => h.celdas.length) && !(leido.texto?.length)) throw new DocumentoNoLeible("vacio");
  // Que quepa: se recortan las hojas más largas hasta bajar del tope.
  let json = JSON.stringify(leido).length;
  while (json > MAX_JSON) {
    const larga = [...leido.hojas].sort((a, b) => b.celdas.length - a.celdas.length)[0];
    if (!larga || larga.celdas.length < 50) break;
    larga.celdas = larga.celdas.slice(0, Math.floor(larga.celdas.length * 0.7));
    larga.recortada = true;
    json = JSON.stringify(leido).length;
  }
  return leido;
}

/** Resumen de una línea para el chip del chat: «2 hojas · 340 filas». */
export function resumenDocumento(d: Pick<DocumentoLeido, "hojas" | "tipo">): { hojas: number; filas: number } {
  return { hojas: d.hojas.length, filas: d.hojas.reduce((s, h) => s + h.filasTotales, 0) };
}

/** sha256 en hexadecimal (la huella del original: el mismo fichero no se guarda dos veces). */
export async function huella(bytes: Uint8Array): Promise<string> {
  const h = await crypto.subtle.digest("SHA-256", bytes as Uint8Array<ArrayBuffer>);
  return Array.from(new Uint8Array(h)).map((b) => b.toString(16).padStart(2, "0")).join("");
}
